import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Info, Tag } from "lucide-react";
import { toast } from "sonner";
import { DataTable, money, money2, fmt, errMsg } from "./shared";

const ORIG_PCT = { PRODUTO: "definida no produto", PADRAO_EMPRESA: "padrão da empresa", NAO_CONFIGURADA: "não configurada" };

export function ComponentesAviso({ texto, testid = "custo-componentes" }) {
  return <div className="flex gap-2 text-xs rounded-md border border-sky-500/30 bg-sky-500/5 p-2 text-sky-100" data-testid={testid}><Info className="w-4 h-4 shrink-0 text-sky-300" /><span>{texto}</span></div>;
}

export function PrecoBox({ p, titulo = "Preço sugerido", testid = "preco" }) {
  if (!p) return null;
  return (
    <div className="rounded-md border border-primary/40 bg-primary/5 p-3 space-y-1 text-sm" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-bold uppercase text-primary"><Tag className="w-4 h-4" /> {titulo} · margem sobre a venda</div>
      {p.preco_sugerido == null ? <div className="text-amber-300 text-xs" data-testid={`${testid}-sem-margem`}>Margem não configurada — defina no produto acabado ou o padrão da empresa.</div> : (
        <>
          <div className="flex justify-between"><span>Custo unitário</span><b className="font-mono" data-testid={`${testid}-custo`}>{money(p.custo_unit)}</b></div>
          <div className="flex justify-between"><span>Margem sobre a venda ({ORIG_PCT[p.origem_pct]})</span><b className="font-mono" data-testid={`${testid}-margem`}>{fmt(p.margem_pct, 2)}%</b></div>
          <div className="flex justify-between text-base border-t border-border pt-1"><span>Preço sugerido</span><b className="font-mono text-primary" data-testid={`${testid}-valor`}>{money(p.preco_sugerido)}</b></div>
          <div className="text-[11px] text-muted-foreground" data-testid={`${testid}-formula`}>{p.formula}: {money(p.custo_unit)} ÷ (1 − {fmt(p.margem_pct, 2)}%) = {money(p.preco_exato)} · margem {money(p.lucro_unit)}/un ({fmt(p.margem_pct, 2)}% do preço). Equivale a markup de {fmt(p.markup_equivalente_pct, 2)}% sobre o custo — não é o mesmo percentual.</div>
          <div className="text-[11px] text-amber-200" data-testid={`${testid}-nao-lucro`}>A margem considera apenas os materiais incluídos no custo e NÃO representa lucro líquido.</div>
          <div className="flex flex-wrap gap-1 items-center text-[11px]" data-testid={`${testid}-excluidos`}><span className="text-muted-foreground">Fora do custo:</span>
            {(p.custos_excluidos || ["Mão de obra", "Energia", "Impostos", "Frete"]).map((c) => <span key={c} className="px-1.5 py-0.5 rounded border border-red-500/40 text-red-200 line-through decoration-red-400/60">{c}</span>)}</div>
        </>
      )}
    </div>
  );
}

export function MargemPadrao({ aprov }) {
  const [v, setV] = useState("");
  useEffect(() => { api.get("/producao/config").then((r) => setV(r.data.margem_padrao_pct ?? "")); }, []);
  const salvar = async () => {
    try { await api.put("/producao/config", { margem_padrao_pct: v === "" ? null : Number(v) }); toast.success("Margem padrão salva"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex items-center gap-2 text-xs" data-testid="margem-padrao">
      <span className="text-muted-foreground">Margem padrão s/ venda</span>
      <Input type="number" className="w-20 h-8" value={v} disabled={!aprov} onChange={(e) => setV(e.target.value)} data-testid="margem-padrao-input" /><span>%</span>
      {aprov && <Button size="sm" variant="outline" onClick={salvar} data-testid="margem-padrao-salvar">Salvar</Button>}
    </div>
  );
}

export function MemoriaOP({ op }) {
  const m = op.memoria_custo;
  if (!m) return null;
  const cols = [{ key: "item", label: "Material" }, { key: "previsto", label: "Previsto", align: "right" }, { key: "custo_unit_previsto", label: "Custo prev.", align: "right" },
    { key: "valor_previsto", label: "Valor prev.", align: "right" }, { key: "consumido", label: "Consumido", align: "right" }, { key: "perda", label: "Perda", align: "right" },
    { key: "custo_unit_real", label: "Custo real/un", align: "right" }, { key: "valor_real", label: "Valor real", align: "right" }];
  const lin = (l, v, t) => <div className="flex justify-between"><span>{l}</span><b className="font-mono" data-testid={t}>{v}</b></div>;
  return (
    <Card className="bg-card/70 p-0 overflow-hidden" data-testid="op-memoria-custo">
      <div className="px-4 py-2 text-xs font-bold uppercase text-primary">Memória de cálculo do custo {m.previa ? "(prévia — ordem não encerrada)" : "(gravada no encerramento)"}</div>
      <DataTable colunas={cols} linhas={m.linhas} testid="memoria-linhas" render={{
        item: (r) => <span>{r.item} <span className="text-xs text-muted-foreground">({r.unidade})</span></span>,
        previsto: (r) => fmt(r.previsto), consumido: (r) => fmt(r.consumido), perda: (r) => fmt(r.perda),
        custo_unit_previsto: (r) => money(r.custo_unit_previsto), valor_previsto: (r) => money2(r.valor_previsto), custo_unit_real: (r) => money(r.custo_unit_real), valor_real: (r) => money2(r.valor_real) }} />
      <div className="grid md:grid-cols-3 gap-3 p-4">
        <div className="space-y-1 text-sm rounded-md border border-border p-3">
          {lin("Custo previsto total", money2(m.custo_previsto_total), "mem-prev-total")}
          {lin(`Qtd planejada`, fmt(m.qtd_planejada), "mem-qtd-plan")}
          {lin("Custo previsto / un", money(m.custo_previsto_unit), "mem-prev-un")}
        </div>
        <div className="space-y-1 text-sm rounded-md border border-border p-3">
          {lin("Custo real total", money2(m.custo_real_total), "mem-real-total")}
          {m.credito_transformado > 0 && lin("Crédito sobra transformada", `− ${money(m.credito_transformado)}`, "mem-credito")}
          {lin("Qtd aprovada", m.qtd_aprovada == null ? "—" : fmt(m.qtd_aprovada), "mem-qtd-aprov")}
          {lin("Rendimento real", m.rendimento_pct == null ? "—" : `${fmt(m.rendimento_pct, 2)}%`, "mem-rendimento")}
          {lin("Custo real / un aprovada", money(m.custo_real_unit), "mem-real-un")}
        </div>
        <div className="space-y-2">
          <PrecoBox p={m.custo_real_unit != null ? op.precificacao : op.precificacao_prevista} titulo={m.custo_real_unit != null ? "Preço sugerido (custo real)" : "Preço sugerido (custo previsto)"} testid="op-preco" />
          {m.custo_real_unit != null && op.precificacao_prevista?.preco_sugerido != null && <div className="text-xs rounded-md border border-border p-2 flex justify-between" data-testid="op-preco-previsto">
            <span>Preço sugerido previsto ({money(op.precificacao_prevista.custo_unit)} ÷ (1 − {fmt(op.precificacao_prevista.margem_pct, 2)}%))</span><b className="font-mono">{money2(op.precificacao_prevista.preco_sugerido)}</b></div>}
        </div>
      </div>
      <div className="px-4 pb-4 space-y-2">
        <div className="text-[11px] text-muted-foreground" data-testid="mem-formula">{m.formula} {m.criterio}.</div>
        <ComponentesAviso texto={m.componentes} testid="op-componentes" />
      </div>
    </Card>
  );
}
